// src/features/secureDocuments/validation/lohnlaufPruefung.ts
//
// Prüfung von DATEV-Lohnlaufunterlagen vor dem Upload in den geschützten
// Downloadbereich. Das Modul ist bewusst frei von Oberflächenbezug, damit es
// später auch in der DATEV-Automatisierung und beim Portal-Upload aus dem
// Ticket verwendet werden kann.
//
// Die Textextraktion nutzt dieselbe pdfjs-Einbindung wie
// datevInvoiceParserService und payrollPdfAnalyzerService (Worker über cdnjs,
// Version aus dem Paket). Die Zeilenrekonstruktion ist hier eigenständig, weil
// die SEPA-Abstimmlisten um 90 Grad gedreht im PDF liegen und ohne Auswertung
// der Textmatrix Spalten statt Zeilen liefern.
//
// Sofortige Ausführung: Das Upload-Modal kann SEPA-Dateien mit einem
// Ausführungstermin nach dem Prüftag auf den Versandtag umstellen
// (sepaSofortAusfuehrung.ts). Geprüft wird trotzdem immer die
// DATEV-Originaldatei, der Abgleich mit dem Begleitzettel bleibt also streng.
// Für umgestellte Dateien entfällt lediglich die Warnung zum späteren
// Ausführungstermin; stattdessen erscheint ein Hinweis, weil der Begleitzettel
// weiterhin den ursprünglichen Termin nennt.

import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ============================================
// ÖFFENTLICHE TYPEN
// ============================================

/**
 * Sperre blockiert den Upload. Warnung und Hinweis sind informativ.
 * Ungeprüft bedeutet, dass die Prüfung selbst nicht durchlaufen konnte; das
 * blockiert bewusst nicht, wird aber sichtbar ausgewiesen.
 */
export type Pruefstufe = 'sperre' | 'warnung' | 'hinweis' | 'ungeprueft' | 'ok';

export interface Befund {
  stufe: Pruefstufe;
  titel: string;
  detail?: string;
}

export interface Pruefkennzahlen {
  mandantennummer: string | null;
  kennziffer: string | null;
  zeitraum: string | null;
  lohnsumme: number | null;
  anzahlUeberweisungen: number | null;
  anzahlLohnscheine: number | null;
  ausfuehrungstermine: string[];
  auftraggeberIban: string | null;
}

/**
 * SEPA-Datei, deren Ausführungstermin nach dem Prüftag liegt. Grundlage für
 * das Angebot im Modal, die Datei sofort ausführen zu lassen. Ermittelt wird
 * immer aus der DATEV-Originaldatei, unabhängig von einer bereits gewählten
 * sofortigen Ausführung.
 */
export interface SpaeterAuszufuehren {
  /** Dateiname genau wie in der Auswahl (File.name). */
  dateiname: string;
  zahlungsart: string;
  /** Ursprünglicher Ausführungstermin als YYYY-MM-DD. */
  termin: string;
}

/**
 * Vom Modal gewählte sofortige Ausführung: Diese SEPA-Dateien werden beim
 * Upload auf den Ausführungstermin termin (YYYY-MM-DD, Versandtag) umgestellt.
 */
export interface SofortAusfuehrung {
  /** Dateinamen genau wie in der Auswahl (File.name). */
  dateinamen: string[];
  termin: string;
}

export interface PruefErgebnis {
  /** True, sobald mindestens eine Datei dem DATEV-Muster folgt. */
  aktiv: boolean;
  befunde: Befund[];
  hatSperre: boolean;
  kennzahlen: Pruefkennzahlen;
  /** Dateien, die nicht dem DATEV-Muster folgen und deshalb ungeprüft bleiben. */
  nichtGeprueft: string[];
  /** SEPA-Dateien mit einem Ausführungstermin nach dem Prüftag. */
  spaeterAuszufuehren: SpaeterAuszufuehren[];
}

export interface Kundenstammdaten {
  beraternummer: string | null;
  mandantennummer: string | null;
}

// ============================================
// HILFSFUNKTIONEN
// ============================================

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

function monatsIndex(name: string): number {
  const n = String(name).normalize('NFC').toLowerCase();
  return MONATE.findIndex(m => m.toLowerCase() === n) + 1;
}

/** Wandelt einen deutschen Betrag wie "18.219,95" in eine Zahl. */
function zahl(text: string | undefined | null): number | null {
  if (text === undefined || text === null) return null;
  const wert = Number(String(text).trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(wert) ? wert : null;
}

export function euro(wert: number | null): string {
  if (wert === null || wert === undefined) return '–';
  return wert.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function isoZuDeutsch(iso: string | null): string {
  const treffer = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return treffer ? `${treffer[3]}.${treffer[2]}.${treffer[1]}` : (iso || '–');
}

function deutschZuIso(datum: string | null): string | null {
  const treffer = String(datum || '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return treffer ? `${treffer[3]}-${treffer[2]}-${treffer[1]}` : null;
}

/** Prüfziffernverfahren nach ISO 7064 Mod 97-10. */
export function ibanGueltig(roh: string | null): boolean {
  const wert = String(roh || '').replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[0-9A-Z]{10,30}$/.test(wert)) return false;
  let rest = 0;
  for (const zeichen of wert.slice(4) + wert.slice(0, 4)) {
    const ersatz = zeichen >= '0' && zeichen <= '9' ? zeichen : String(zeichen.charCodeAt(0) - 55);
    for (const ziffer of ersatz) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest === 1;
}

export function ibanLesbar(roh: string | null): string {
  return String(roh || '').replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

// ============================================
// DATEINAME
// ============================================

interface Dateiname {
  basis: string;
  systemdatei: boolean;
  erkannt: boolean;
  kind?: 'pdf' | 'xml';
  mandant?: string;
  monat?: number;
  jahr?: number;
  kennziffer?: string;
  art?: string;
  zahlungsart?: string;
}

/**
 * macOS liefert Dateinamen in zerlegter Unicode-Form (NFD). Ohne
 * Normalisierung scheitert jeder Vergleich auf Umlaute, etwa bei
 * "Vermögensw__Leistungen.xml".
 */
function parseDateiname(roh: string): Dateiname {
  const basis = String(roh).normalize('NFC').split('/').pop() || '';
  if (/^\._/.test(basis) || basis === '.DS_Store') {
    return { basis, systemdatei: true, erkannt: false };
  }
  const alsPdf = basis.match(/^(\d{5})_(\d{2})_(\d{4})_(.+)_([A-Z0-9]{3})\.pdf$/i);
  if (alsPdf) {
    return {
      basis, systemdatei: false, erkannt: true, kind: 'pdf',
      mandant: alsPdf[1], monat: parseInt(alsPdf[2], 10), jahr: parseInt(alsPdf[3], 10),
      art: alsPdf[4], kennziffer: alsPdf[5].toUpperCase()
    };
  }
  const alsXml = basis.match(/^SEPA-(\d{5})-(\d{4})_(\d{2})-([A-Z0-9]{3})-(.+)\.xml$/i);
  if (alsXml) {
    return {
      basis, systemdatei: false, erkannt: true, kind: 'xml',
      mandant: alsXml[1], jahr: parseInt(alsXml[2], 10), monat: parseInt(alsXml[3], 10),
      kennziffer: alsXml[4].toUpperCase(), zahlungsart: alsXml[5]
    };
  }
  return { basis, systemdatei: false, erkannt: false };
}

/** Gate für den Aufrufer: Folgt die Datei überhaupt dem DATEV-Muster? */
export function istLohnlaufDatei(dateiname: string): boolean {
  return parseDateiname(dateiname).erkannt;
}

/**
 * Vereinheitlicht die Zahlungsart aus Dateiname und PDF, damit
 * "Vermögensw__Leistungen" und "Vermögensw. Leistungen" zusammenfinden.
 */
function zahlungsartNormal(roh: string | null | undefined): string {
  const text = String(roh || '').normalize('NFC').toLowerCase()
    .replace(/[_\s.]+/g, '')
    .replace(/ö/g, 'o').replace(/ä/g, 'a').replace(/ü/g, 'u');
  if (text.startsWith('lohn')) return 'Lohn/Gehalt';
  if (text.startsWith('vermogensw')) return 'Vermögenswirksame Leistungen';
  if (text.startsWith('individuelle')) return 'Individuelle Zahlungen';
  return String(roh || '').normalize('NFC');
}

// ============================================
// PDF-TEXTEXTRAKTION
// ============================================

/**
 * Bestimmt je Textstück die visuelle Zeile und die Position darin. Die
 * SEPA-Abstimmlisten legt DATEV um 90 Grad gedreht ab; ohne diese Umrechnung
 * liefert die Extraktion dort Spalten statt Zeilen.
 */
function lage(transform: number[]): { zeile: number; spur: number } {
  const a = transform[0];
  const b = transform[1];
  const x = transform[4];
  const y = transform[5];
  if (Math.abs(a) >= Math.abs(b)) {
    return a >= 0 ? { zeile: -y, spur: x } : { zeile: y, spur: -x };
  }
  return b > 0 ? { zeile: x, spur: y } : { zeile: -x, spur: -y };
}

async function pdfSeitenZeilen(daten: Uint8Array): Promise<string[][]> {
  const dokument = await pdfjsLib.getDocument({ data: daten }).promise;
  const seiten: string[][] = [];

  for (let nummer = 1; nummer <= dokument.numPages; nummer++) {
    const seite = await dokument.getPage(nummer);
    const inhalt = await seite.getTextContent();

    const stuecke: Array<{ zeile: number; spur: number; text: string }> = [];
    for (const posten of inhalt.items) {
      if (!posten.str || !posten.str.trim()) continue;
      const position = lage(posten.transform);
      stuecke.push({ zeile: position.zeile, spur: position.spur, text: posten.str });
    }

    stuecke.sort((links, rechts) => links.zeile - rechts.zeile);

    const gruppen: Array<{ zeile: number; posten: typeof stuecke }> = [];
    for (const stueck of stuecke) {
      const letzte = gruppen[gruppen.length - 1];
      if (letzte && Math.abs(letzte.zeile - stueck.zeile) <= 2.5) letzte.posten.push(stueck);
      else gruppen.push({ zeile: stueck.zeile, posten: [stueck] });
    }

    seiten.push(
      gruppen
        .map(gruppe => gruppe.posten
          .sort((links, rechts) => links.spur - rechts.spur)
          .map(posten => posten.text)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim())
        .filter(zeile => zeile.length > 0)
    );
  }

  return seiten;
}

// ============================================
// PDF-INHALT
// ============================================

interface Begleitzettel {
  zahlungsart: string;
  // Kennziffer von der Begleitzettel-Seite selbst. Sie ist der Bezugspunkt für
  // den strengen Abgleich mit der zugehörigen SEPA-Datei.
  kennziffer: string | null;
  monat: number | null;
  jahr: number | null;
  auftraggeber: string | null;
  iban: string | null;
  ausfuehrung: string | null;
  anzahl: number;
  gesamt: number | null;
}

interface Abstimmzeile {
  personalnummer: string;
  name: string;
  iban: string;
  betrag: number | null;
}

interface Abstimmliste {
  zahlungsart: string;
  zeilen: Abstimmzeile[];
  anzahl: number | null;
  summe: number | null;
}

interface Lohnschein {
  kennziffer: string | null;
  personalnummer: string | null;
  iban: string | null;
  auszahlung: number | null;
}

interface PdfInhalt {
  berater: Set<string>;
  mandanten: Set<string>;
  kennziffern: Set<string>;
  perioden: Set<string>;
  begleitzettel: Begleitzettel[];
  abstimmlisten: Abstimmliste[];
  lohnscheine: Lohnschein[];
}

async function parsePdf(daten: Uint8Array): Promise<PdfInhalt> {
  const seiten = await pdfSeitenZeilen(daten);
  const inhalt: PdfInhalt = {
    berater: new Set(), mandanten: new Set(), kennziffern: new Set(), perioden: new Set(),
    begleitzettel: [], abstimmlisten: [], lohnscheine: []
  };

  for (const zeilen of seiten) {
    const text = zeilen.join('\n');

    const beraterTreffer = text.matchAll(/Berater:\s*(\d{4,8})/g);
    for (const treffer of Array.from(beraterTreffer)) inhalt.berater.add(treffer[1]);

    const mandantTreffer = text.matchAll(/Mandant:\s*(\d{3,7})/g);
    for (const treffer of Array.from(mandantTreffer)) inhalt.mandanten.add(treffer[1]);

    const kopfTreffer = text.matchAll(/\b(\d{4,8})\/(\d{3,7})\/(\d{1,6})\b/g);
    for (const treffer of Array.from(kopfTreffer)) {
      inhalt.berater.add(treffer[1]);
      inhalt.mandanten.add(treffer[2]);
    }

    const kennzifferTreffer = text.matchAll(/VKZ:\s*([A-Z0-9]{3})/g);
    for (const treffer of Array.from(kennzifferTreffer)) inhalt.kennziffern.add(treffer[1]);

    // Begleitzettel "Summe Zahlungsauftrag"
    if (/Summe Zahlungsauftrag f/.test(text)) {
      const periode = text.match(/Summe Zahlungsauftrag f\S*r (\S+) (\d{4})/);
      const index = zeilen.findIndex(zeile => /^SEPA-Dateiexport/.test(zeile));
      const monat = periode ? monatsIndex(periode[1]) : null;
      const jahr = periode ? parseInt(periode[2], 10) : null;
      if (periode && monat) inhalt.perioden.add(`${jahr}-${String(monat).padStart(2, '0')}`);

      inhalt.begleitzettel.push({
        zahlungsart: zahlungsartNormal(index >= 0 ? zeilen[index + 1] : null),
        kennziffer: (text.match(/VKZ:\s*([A-Z0-9]{3})/) || [])[1] || null,
        monat,
        jahr,
        auftraggeber: (text.match(/Auftraggeber:\s*(.+)/) || [])[1] || null,
        iban: (text.match(/IBAN:\s*([A-Z]{2}\d{2}(?: [0-9A-Z]{4})*(?: [0-9A-Z]{1,4})?)/) || [])[1] || null,
        ausfuehrung: (text.match(/Ausf\S*hrungstermin:\s*(\d{2}\.\d{2}\.\d{4})/) || [])[1] || null,
        anzahl: Number((text.match(/Anzahl Zahlungss\S*tze:\s*(\d+)/) || [])[1] ?? NaN),
        gesamt: zahl((text.match(/Gesamtbetrag:\s*([\d.]*\d,\d{2})/) || [])[1])
      });
    }

    // SEPA-Abstimmliste
    if (/SEPA Abstimmliste Dateiexport/.test(text)) {
      const kopf = text.match(/SEPA Abstimmliste Dateiexport (.+?) (\S+) (\d{4})/);
      const liste: Abstimmliste = {
        zahlungsart: kopf ? zahlungsartNormal(kopf[1]) : '',
        zeilen: [], anzahl: null, summe: null
      };
      if (kopf) {
        const monat = monatsIndex(kopf[2]);
        if (monat) inhalt.perioden.add(`${parseInt(kopf[3], 10)}-${String(monat).padStart(2, '0')}`);
      }
      for (const zeile of zeilen) {
        const satz = zeile.match(
          /^(\d{2,6}) (.+?) ([A-Z]{2}\d{2}(?: [0-9A-Z]{4})*(?: [0-9A-Z]{1,4})?)(?: ([A-Z]{6}[0-9A-Z]{2,5}))? ([\d.]*\d,\d{2})(?: ([A-Z]{4}))? ([A-Z0-9]{3})$/
        );
        if (satz) {
          liste.zeilen.push({
            personalnummer: satz[1],
            name: satz[2].trim(),
            iban: satz[3].replace(/\s/g, ''),
            betrag: zahl(satz[5])
          });
        }
        const summe = zeile.match(/Zahlungsvorg\S*nge Anzahl:\s*(\d+)\s*Gesamtsumme:\s*([\d.]*\d,\d{2})/);
        if (summe) {
          liste.anzahl = parseInt(summe[1], 10);
          liste.summe = zahl(summe[2]);
        }
      }
      inhalt.abstimmlisten.push(liste);
    }

    // Lohnschein
    if (/Abrechnung der Brutto\/Netto-Bez/.test(text)) {
      const kopf = text.match(/\b([A-Z0-9]{3}) (\d{4,8})\/(\d{3,7})\/(\d{1,6})\b/);
      const periode = text.match(/Brutto\/Netto-Bez\S*ge f\S*r (\S+) (\d{4})/);
      const kontozeile = zeilen.find(zeile => /^Konto /.test(zeile)) || '';
      const betraege = Array.from(kontozeile.matchAll(/([\d.]*\d,\d{2})/g)).map(t => zahl(t[1]));

      if (periode) {
        const monat = monatsIndex(periode[1]);
        if (monat) inhalt.perioden.add(`${parseInt(periode[2], 10)}-${String(monat).padStart(2, '0')}`);
      }
      if (kopf) inhalt.kennziffern.add(kopf[1]);

      const ibanTreffer = kontozeile.match(/^Konto ([A-Z]{2}\d{2}(?: [0-9A-Z]{4})*(?: [0-9A-Z]{1,4})?)/);
      inhalt.lohnscheine.push({
        kennziffer: kopf ? kopf[1] : null,
        personalnummer: kopf ? kopf[4] : null,
        iban: ibanTreffer ? ibanTreffer[1].replace(/\s/g, '') : null,
        auszahlung: betraege.length > 0 ? betraege[betraege.length - 1] : null
      });
    }
  }

  return inhalt;
}

// ============================================
// SEPA-XML
// ============================================

interface SepaZahlung {
  name: string | null;
  iban: string;
  betrag: number;
}

interface SepaInhalt {
  nachrichtenId: string | null;
  kopfAnzahl: number;
  kopfSumme: number;
  auftragAnzahl: number;
  auftragSumme: number;
  ausfuehrung: string | null;
  auftraggeber: string | null;
  auftraggeberIban: string | null;
  zahlungen: SepaZahlung[];
}

function parseSepa(text: string): SepaInhalt | null {
  const dom = new DOMParser().parseFromString(text, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length > 0) return null;

  const lies = (knoten: Element | Document, tag: string): string | null => {
    const treffer = knoten.getElementsByTagName(tag);
    return treffer.length > 0 ? (treffer[0].textContent || '').trim() : null;
  };

  const kopf = dom.getElementsByTagName('GrpHdr')[0];
  const auftrag = dom.getElementsByTagName('PmtInf')[0];
  if (!kopf || !auftrag) return null;

  const auftraggeber = auftrag.getElementsByTagName('Dbtr')[0];
  const konto = auftrag.getElementsByTagName('DbtrAcct')[0];

  const zahlungen: SepaZahlung[] = [];
  const posten = auftrag.getElementsByTagName('CdtTrfTxInf');
  for (let i = 0; i < posten.length; i++) {
    zahlungen.push({
      name: lies(posten[i], 'Nm'),
      iban: (lies(posten[i], 'IBAN') || '').replace(/\s/g, ''),
      betrag: Number(lies(posten[i], 'InstdAmt'))
    });
  }

  return {
    nachrichtenId: lies(kopf, 'MsgId'),
    kopfAnzahl: Number(lies(kopf, 'NbOfTxs')),
    kopfSumme: Number(lies(kopf, 'CtrlSum')),
    auftragAnzahl: Number(lies(auftrag, 'NbOfTxs')),
    auftragSumme: Number(lies(auftrag, 'CtrlSum')),
    ausfuehrung: lies(auftrag, 'ReqdExctnDt'),
    auftraggeber: auftraggeber ? (auftraggeber.textContent || '').trim() : null,
    auftraggeberIban: konto ? (konto.textContent || '').replace(/\s/g, '') : null,
    zahlungen
  };
}

// ============================================
// PRÜFUNG
// ============================================

interface GelesenDatei {
  name: Dateiname;
  /** Dateiname genau wie in der Auswahl (File.name), ohne Normalisierung. */
  dateiname: string;
  groesse: number;
  pdf?: PdfInhalt;
  sepa?: SepaInhalt;
  leseFehler?: string;
  typFalsch?: boolean;
}

const LEER_ERGEBNIS: PruefErgebnis = {
  aktiv: false,
  befunde: [],
  hatSperre: false,
  kennzahlen: {
    mandantennummer: null, kennziffer: null, zeitraum: null, lohnsumme: null,
    anzahlUeberweisungen: null, anzahlLohnscheine: null, ausfuehrungstermine: [], auftraggeberIban: null
  },
  nichtGeprueft: [],
  spaeterAuszufuehren: []
};

/**
 * Prüft die übergebenen Dateien gegen den ausgewählten Kunden. Gibt ein
 * Ergebnis mit aktiv=false zurück, wenn keine Datei dem DATEV-Muster folgt.
 *
 * sofort nennt die SEPA-Dateien, die das Modal beim Upload auf den
 * Versandtag umstellt. Ohne Angabe bleibt die Prüfung wie bisher.
 */
export async function pruefeLohnlauf(
  dateien: File[],
  stammdaten: Kundenstammdaten | null,
  heute: Date,
  sofort: SofortAusfuehrung | null = null
): Promise<PruefErgebnis> {
  const nichtGeprueft: string[] = [];
  const gelesen: GelesenDatei[] = [];

  for (const datei of dateien) {
    const name = parseDateiname(datei.name);
    if (!name.erkannt) {
      nichtGeprueft.push(name.basis);
      continue;
    }

    const eintrag: GelesenDatei = { name, dateiname: datei.name, groesse: datei.size };
    try {
      const puffer = await datei.arrayBuffer();
      const anfang = new TextDecoder().decode(new Uint8Array(puffer.slice(0, 5)));
      if (name.kind === 'pdf') {
        if (anfang !== '%PDF-') eintrag.typFalsch = true;
        eintrag.pdf = await parsePdf(new Uint8Array(puffer));
      } else {
        const text = new TextDecoder('utf-8').decode(puffer);
        if (!text.trimStart().startsWith('<')) eintrag.typFalsch = true;
        const sepa = parseSepa(text);
        if (!sepa) eintrag.leseFehler = 'SEPA-Struktur nicht erkannt';
        else eintrag.sepa = sepa;
      }
    } catch (fehler) {
      eintrag.leseFehler = fehler instanceof Error ? fehler.message : 'Datei konnte nicht gelesen werden';
    }
    gelesen.push(eintrag);
  }

  if (gelesen.length === 0) {
    return { ...LEER_ERGEBNIS, nichtGeprueft };
  }

  const befunde: Befund[] = [];
  const melde = (stufe: Pruefstufe, titel: string, detail?: string) => befunde.push({ stufe, titel, detail });

  const pdfs = gelesen.filter(d => d.pdf);
  const xmls = gelesen.filter(d => d.sepa);
  const tagesbeginn = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate());

  // --- Dateiebene ---
  for (const datei of gelesen) {
    if (datei.leseFehler) melde('ungeprueft', 'Datei nicht lesbar', `${datei.name.basis}: ${datei.leseFehler}`);
    if (datei.groesse === 0) melde('sperre', 'Datei ist leer', datei.name.basis);
    if (datei.typFalsch) melde('sperre', 'Dateiinhalt passt nicht zur Endung', datei.name.basis);
  }

  // --- Zuordnung zum ausgewählten Kunden ---
  const erwarteterMandant = stammdaten?.mandantennummer?.trim() || null;
  const erwarteterBerater = stammdaten?.beraternummer?.trim() || null;

  if (!erwarteterMandant) {
    melde('ungeprueft', 'Mandantennummer beim Kunden nicht hinterlegt',
      'Die Zuordnung der Unterlagen zum Kunden konnte nicht geprüft werden.');
  } else {
    for (const datei of gelesen) {
      if (datei.name.mandant !== erwarteterMandant) {
        melde('sperre', 'Datei gehört zu einem anderen Mandanten',
          `${datei.name.basis}: Dateiname nennt ${datei.name.mandant}, der Kunde hat ${erwarteterMandant}`);
      }
    }
    for (const datei of pdfs) {
      const imInhalt = Array.from(datei.pdf!.mandanten);
      if (imInhalt.length === 0) {
        melde('ungeprueft', 'Mandantennummer im Dateiinhalt nicht gefunden', datei.name.basis);
      } else if (imInhalt.length > 1) {
        melde('sperre', 'Mehrere Mandantennummern in einer Datei',
          `${datei.name.basis}: ${imInhalt.join(', ')}`);
      } else if (imInhalt[0] !== erwarteterMandant) {
        melde('sperre', 'Mandantennummer im Dateiinhalt weicht vom Kunden ab',
          `${datei.name.basis}: Inhalt ${imInhalt[0]}, Kunde ${erwarteterMandant}`);
      }
    }
    for (const datei of xmls) {
      const treffer = String(datei.sepa!.nachrichtenId || '').match(/-(\d{5})\d{6,}$/);
      if (!treffer) {
        melde('ungeprueft', 'Mandantennummer in der Nachrichtenkennung nicht lesbar',
          `${datei.name.basis}: ${datei.sepa!.nachrichtenId}`);
      } else if (treffer[1] !== erwarteterMandant) {
        melde('sperre', 'Mandantennummer in der SEPA-Nachrichtenkennung weicht ab',
          `${datei.name.basis}: ${treffer[1]} statt ${erwarteterMandant}`);
      }
    }
  }

  const beraterImInhalt = new Set(pdfs.flatMap(d => Array.from(d.pdf!.berater)));
  if (beraterImInhalt.size > 1) {
    melde('sperre', 'Uneinheitliche Beraternummer in den Unterlagen', Array.from(beraterImInhalt).join(', '));
  } else if (beraterImInhalt.size === 1 && erwarteterBerater) {
    const gefunden = Array.from(beraterImInhalt)[0];
    if (gefunden !== erwarteterBerater) {
      melde('sperre', 'Beraternummer weicht vom Kunden ab',
        `Unterlagen ${gefunden}, Kunde ${erwarteterBerater}`);
    }
  } else if (beraterImInhalt.size === 0 && pdfs.length > 0) {
    melde('ungeprueft', 'Beraternummer in den Unterlagen nicht gefunden');
  } else if (!erwarteterBerater) {
    melde('ungeprueft', 'Beraternummer beim Kunden nicht hinterlegt');
  }

  // --- Abrechnungszeitraum ---
  const perioden = new Set<string>([
    ...gelesen.map(d => `${d.name.jahr}-${String(d.name.monat).padStart(2, '0')}`),
    ...pdfs.flatMap(d => Array.from(d.pdf!.perioden))
  ]);
  if (perioden.size > 1) {
    melde('sperre', 'Uneinheitlicher Abrechnungszeitraum', Array.from(perioden).sort().join(', '));
  }

  // --- Kennziffer ---
  // Streng geprüft wird nur die Paarung SEPA-Datei zu Begleitzettel; das
  // geschieht weiter unten in der Paarungsschleife, wo beide Seiten bekannt
  // sind. Eine abweichende Kennziffer auf den Lohnscheinen ist zulässig, weil
  // Lohnabrechnung und Zahlungslauf in DATEV aus getrennten Verarbeitungen
  // stammen können. Sie wird deshalb nur gemeldet, blockiert aber nicht.
  // Die inhaltliche Zusammengehörigkeit des Pakets sichern nach wie vor die
  // Abgleiche über Anzahl, Gesamtbetrag, Auftraggeberkonto, Ausführungstermin
  // und die zeilenweise Gegenprobe gegen die Abstimmliste.
  const kennziffernZahlung = new Set<string>();
  for (const datei of xmls) {
    if (datei.name.kennziffer) kennziffernZahlung.add(datei.name.kennziffer);
  }
  for (const datei of pdfs) {
    for (const zettelEintrag of datei.pdf!.begleitzettel) {
      if (zettelEintrag.kennziffer) kennziffernZahlung.add(zettelEintrag.kennziffer);
    }
  }
  const kennziffernLohnschein = new Set<string>();
  for (const datei of pdfs) {
    for (const schein of datei.pdf!.lohnscheine) {
      if (schein.kennziffer) kennziffernLohnschein.add(schein.kennziffer);
    }
  }
  if (kennziffernZahlung.size > 1) {
    melde('sperre', 'Uneinheitliche Kennziffer auf der Zahlungsseite',
      `SEPA-Dateien und Begleitzettel tragen ${Array.from(kennziffernZahlung).sort().join(', ')}.`);
  }
  if (kennziffernLohnschein.size > 1) {
    melde('hinweis', 'Uneinheitliche Kennziffer auf den Lohnscheinen',
      Array.from(kennziffernLohnschein).sort().join(', '));
  }
  // Der Dateiname eines PDFs wird für den strengen Abgleich nicht mehr
  // herangezogen. Damit eine umbenannte oder aus einem anderen Lauf
  // hineingeratene Datei trotzdem auffällt, wird geprüft, ob die Kennziffer
  // im Dateinamen überhaupt irgendwo im Inhalt derselben Datei vorkommt.
  for (const datei of pdfs) {
    const imDateinamen = datei.name.kennziffer;
    if (!imDateinamen) continue;
    const imInhalt = new Set<string>(Array.from(datei.pdf!.kennziffern));
    if (imInhalt.size > 0 && !imInhalt.has(imDateinamen)) {
      melde('hinweis', 'Kennziffer im Dateinamen kommt im Dateiinhalt nicht vor',
        `${datei.name.basis}: Dateiname ${imDateinamen}, Inhalt ${Array.from(imInhalt).sort().join(', ')}`);
    }
  }

  if (kennziffernZahlung.size > 0 && kennziffernLohnschein.size > 0) {
    const abweichend = Array.from(kennziffernLohnschein).filter(k => !kennziffernZahlung.has(k));
    if (abweichend.length > 0) {
      melde('hinweis', 'Lohnscheine tragen eine andere Kennziffer als der Zahlungslauf',
        `Zahlungslauf ${Array.from(kennziffernZahlung).sort().join(', ')}, Lohnscheine ${abweichend.sort().join(', ')}.`);
    }
  }

  // --- SEPA-Datei in sich ---
  for (const datei of xmls) {
    const sepa = datei.sepa!;
    if (!(sepa.kopfAnzahl === sepa.auftragAnzahl && sepa.auftragAnzahl === sepa.zahlungen.length)) {
      melde('sperre', 'Zahlungsanzahl in der SEPA-Datei widersprüchlich',
        `${datei.name.basis}: Kopf ${sepa.kopfAnzahl}, Auftrag ${sepa.auftragAnzahl}, tatsächlich ${sepa.zahlungen.length}`);
    }
    const summe = Math.round(sepa.zahlungen.reduce((a, z) => a + z.betrag, 0) * 100) / 100;
    if (Math.abs(summe - sepa.auftragSumme) > 0.004 || Math.abs(sepa.kopfSumme - sepa.auftragSumme) > 0.004) {
      melde('sperre', 'Kontrollsumme passt nicht zu den Einzelbeträgen',
        `${datei.name.basis}: Kontrollsumme ${euro(sepa.auftragSumme)}, Summe der Zahlungen ${euro(summe)}`);
    }
    const kaputt: string[] = [];
    if (!ibanGueltig(sepa.auftraggeberIban)) kaputt.push(`Auftraggeber ${ibanLesbar(sepa.auftraggeberIban)}`);
    for (const zahlung of sepa.zahlungen) {
      if (!ibanGueltig(zahlung.iban)) kaputt.push(`${zahlung.name} ${ibanLesbar(zahlung.iban)}`);
    }
    if (kaputt.length > 0) {
      melde('sperre', 'IBAN-Prüfziffer falsch', `${datei.name.basis}: ${kaputt.join('; ')}`);
    }
  }

  // --- SEPA gegen Begleitzettel ---
  const zettel = pdfs.flatMap(d => d.pdf!.begleitzettel);
  for (const datei of xmls) {
    const art = zahlungsartNormal(datei.name.zahlungsart);
    const passend = zettel.filter(z => z.zahlungsart === art);
    if (passend.length === 0) {
      melde('sperre', 'Kein Begleitzettel zur SEPA-Datei', `${datei.name.basis} (${art})`);
      continue;
    }
    if (passend.length > 1) {
      melde('sperre', 'Mehrere Begleitzettel zur selben Zahlungsart', art);
      continue;
    }
    const zettelEintrag = passend[0];
    const sepa = datei.sepa!;
    if (zettelEintrag.anzahl !== sepa.auftragAnzahl) {
      melde('sperre', 'Anzahl der Zahlungen weicht ab',
        `${art}: Begleitzettel ${zettelEintrag.anzahl}, SEPA-Datei ${sepa.auftragAnzahl}`);
    }
    if (Math.abs((zettelEintrag.gesamt ?? -1) - sepa.auftragSumme) > 0.004) {
      melde('sperre', 'Gesamtbetrag weicht ab',
        `${art}: Begleitzettel ${euro(zettelEintrag.gesamt)}, SEPA-Datei ${euro(sepa.auftragSumme)}`);
    }
    const zettelIban = (zettelEintrag.iban || '').replace(/\s/g, '');
    if (zettelIban && zettelIban !== sepa.auftraggeberIban) {
      melde('sperre', 'Auftraggeberkonto weicht ab',
        `${art}: Begleitzettel ${ibanLesbar(zettelIban)}, SEPA-Datei ${ibanLesbar(sepa.auftraggeberIban)}`);
    }
    const zettelTermin = deutschZuIso(zettelEintrag.ausfuehrung);
    if (zettelTermin && zettelTermin !== sepa.ausfuehrung) {
      melde('sperre', 'Ausführungstermin weicht ab',
        `${art}: Begleitzettel ${zettelEintrag.ausfuehrung}, SEPA-Datei ${isoZuDeutsch(sepa.ausfuehrung)}`);
    }
    // SEPA-Datei und der zu ihr gehörende Begleitzettel müssen dieselbe
    // Kennziffer tragen. Nur diese Paarung wird streng geprüft.
    if (zettelEintrag.kennziffer && datei.name.kennziffer
        && zettelEintrag.kennziffer !== datei.name.kennziffer) {
      melde('sperre', 'Kennziffer von SEPA-Datei und Begleitzettel weicht ab',
        `${art}: Begleitzettel ${zettelEintrag.kennziffer}, SEPA-Datei ${datei.name.kennziffer}`);
    }
  }
  for (const zettelEintrag of zettel) {
    if (!xmls.some(d => zahlungsartNormal(d.name.zahlungsart) === zettelEintrag.zahlungsart)) {
      melde('sperre', 'SEPA-Datei zum Begleitzettel fehlt', zettelEintrag.zahlungsart);
    }
  }

  // --- Ausführungstermin ---
  // Für SEPA-Dateien, die das Modal auf sofortige Ausführung umstellt, gilt
  // der Versandtag als Termin. Statt der Warnung erscheint dann ein Hinweis,
  // weil der Begleitzettel weiterhin den ursprünglichen Termin nennt. Der
  // strenge Abgleich mit dem Begleitzettel weiter oben läuft unverändert auf
  // der DATEV-Originaldatei.
  const spaeterAuszufuehren: SpaeterAuszufuehren[] = [];
  const effektiveTermine = new Map<GelesenDatei, string>();
  for (const datei of xmls) {
    const termin = datei.sepa!.ausfuehrung;
    if (!termin) {
      melde('ungeprueft', 'Ausführungstermin fehlt', datei.name.basis);
      continue;
    }
    const art = zahlungsartNormal(datei.name.zahlungsart);
    const datum = new Date(termin + 'T00:00:00');
    let effektiv = termin;
    if (datum > tagesbeginn) {
      spaeterAuszufuehren.push({ dateiname: datei.dateiname, zahlungsart: art, termin });
      if (sofort && sofort.dateinamen.includes(datei.dateiname)) {
        effektiv = sofort.termin;
        if (sofort.termin === termin) {
          melde('hinweis', 'Ausführungstermin entspricht dem Versandtag',
            `${art}: Ausführung am ${isoZuDeutsch(termin)}`);
        } else {
          melde('hinweis', 'Ausführung auf sofort umgestellt',
            `${art}: Die hochgeladene Datei wird am ${isoZuDeutsch(sofort.termin)} statt am ${isoZuDeutsch(termin)} ausgeführt. Der Begleitzettel nennt weiterhin den ${isoZuDeutsch(termin)}.`);
        }
      } else {
        melde('warnung', 'Wird nicht sofort ausgeführt',
          `${art}: Ausführung erst am ${isoZuDeutsch(termin)}`);
      }
    }
    effektiveTermine.set(datei, effektiv);
    const effektivDatum = new Date(effektiv + 'T00:00:00');
    if (effektivDatum.getDay() === 0 || effektivDatum.getDay() === 6) {
      melde('hinweis', 'Termin fällt auf ein Wochenende',
        `${datei.name.basis}: ${isoZuDeutsch(effektiv)}`);
    }
  }

  // --- Abstimmliste gegen SEPA-Datei ---
  // Eine Abstimmliste kann sich über mehrere Seiten ziehen, die Summenzeile
  // steht nur auf der letzten. Deshalb je Zahlungsart zusammenführen.
  const listen: Abstimmliste[] = [];
  for (const teil of pdfs.flatMap(d => d.pdf!.abstimmlisten)) {
    let liste = listen.find(l => l.zahlungsart === teil.zahlungsart);
    if (!liste) {
      liste = { zahlungsart: teil.zahlungsart, zeilen: [], anzahl: null, summe: null };
      listen.push(liste);
    }
    liste.zeilen.push(...teil.zeilen);
    if (teil.anzahl !== null) liste.anzahl = teil.anzahl;
    if (teil.summe !== null) liste.summe = teil.summe;
  }

  for (const datei of xmls) {
    const art = zahlungsartNormal(datei.name.zahlungsart);
    const liste = listen.find(l => l.zahlungsart === art);
    if (!liste) continue;
    if (liste.anzahl === null) {
      melde('ungeprueft', 'Abstimmliste ohne Summenzeile', art);
      continue;
    }
    if (liste.zeilen.length !== liste.anzahl) {
      melde('ungeprueft', 'Abstimmliste nicht vollständig lesbar',
        `${art}: ${liste.zeilen.length} von ${liste.anzahl} Zeilen erkannt`);
      continue;
    }
    const offen = liste.zeilen.slice();
    const fehlend: string[] = [];
    for (const zahlung of datei.sepa!.zahlungen) {
      const index = offen.findIndex(z => z.iban === zahlung.iban && Math.abs((z.betrag ?? -1) - zahlung.betrag) < 0.004);
      if (index === -1) fehlend.push(`${zahlung.name} ${euro(zahlung.betrag)}`);
      else offen.splice(index, 1);
    }
    if (fehlend.length > 0 || offen.length > 0) {
      melde('sperre', 'Zahlungen stimmen nicht mit der Abstimmliste überein',
        `${art}: nur in der SEPA-Datei ${fehlend.join(', ') || 'keine'}; nur in der Abstimmliste ${offen.map(o => `${o.name} ${euro(o.betrag)}`).join(', ') || 'keine'}`);
    }
  }

  // --- Lohnscheine ---
  const scheine = pdfs.flatMap(d => d.pdf!.lohnscheine);
  const lohnXml = xmls.find(d => zahlungsartNormal(d.name.zahlungsart) === 'Lohn/Gehalt');
  const lohnListe = listen.find(l => l.zahlungsart === 'Lohn/Gehalt');

  if (scheine.length === 0) melde('warnung', 'Keine Brutto/Netto-Abrechnung im Paket');
  if (zettel.length === 0) melde('warnung', 'Keine Auswertungen mit Begleitzettel im Paket');
  if (xmls.length === 0) melde('warnung', 'Keine SEPA-Datei im Paket');

  if (scheine.length > 0 && lohnXml) {
    const ohneAuszahlung = scheine.filter(s => s.auszahlung === 0).length;
    const mitAuszahlung = scheine.filter(s => s.auszahlung !== null && s.auszahlung > 0).length;
    if (mitAuszahlung !== lohnXml.sepa!.auftragAnzahl) {
      melde('warnung', 'Anzahl Lohnscheine passt nicht zur Anzahl Überweisungen',
        `${scheine.length} Lohnscheine, davon ${ohneAuszahlung} ohne Auszahlung und ${mitAuszahlung} mit Auszahlung, aber ${lohnXml.sepa!.auftragAnzahl} Überweisungen`);
    }
    if (scheine.some(s => s.auszahlung === null)) {
      melde('ungeprueft', 'Auszahlungsbetrag auf mindestens einem Lohnschein nicht lesbar');
    } else {
      const summe = Math.round(scheine.reduce((a, s) => a + (s.auszahlung ?? 0), 0) * 100) / 100;
      if (Math.abs(summe - lohnXml.sepa!.auftragSumme) > 0.004) {
        melde('warnung', 'Summe der Auszahlungsbeträge weicht von der Lohnsumme ab',
          `Lohnscheine ${euro(summe)}, SEPA-Datei ${euro(lohnXml.sepa!.auftragSumme)}`);
      }
    }
  }

  if (scheine.length > 0 && lohnListe && lohnListe.zeilen.length > 0) {
    const nummern = new Set(scheine.map(s => s.personalnummer));
    const fehlt = lohnListe.zeilen
      .filter(z => !nummern.has(z.personalnummer))
      .map(z => `${z.personalnummer} ${z.name}`);
    if (fehlt.length > 0) {
      melde('sperre', 'Zu einer Überweisung fehlt der Lohnschein', fehlt.join(', '));
    }
  }

  if (!befunde.some(b => b.stufe === 'sperre' || b.stufe === 'warnung' || b.stufe === 'ungeprueft')) {
    melde('ok', 'Alle Prüfungen bestanden',
      'Zuordnung, Beträge und Kontoverbindungen passen zusammen.');
  }

  const ersteXml = xmls.length > 0 ? xmls[0].sepa! : null;
  const zeitraumWert = Array.from(perioden)[0] || null;

  return {
    aktiv: true,
    befunde,
    hatSperre: befunde.some(b => b.stufe === 'sperre'),
    kennzahlen: {
      mandantennummer: gelesen[0].name.mandant || null,
      // Angezeigt wird die Kennziffer des Zahlungslaufs. Weicht die der
      // Lohnscheine ab, steht sie in Klammern dahinter.
      kennziffer: (() => {
        const zahlung = Array.from(kennziffernZahlung).sort();
        const abweichend = Array.from(kennziffernLohnschein).filter(k => !kennziffernZahlung.has(k)).sort();
        if (zahlung.length === 0) return abweichend.join(', ') || gelesen[0].name.kennziffer || null;
        return zahlung.join(', ') + (abweichend.length > 0 ? ` (Lohnscheine ${abweichend.join(', ')})` : '');
      })(),
      zeitraum: perioden.size === 1 && zeitraumWert
        ? `${MONATE[parseInt(zeitraumWert.slice(5), 10) - 1]} ${zeitraumWert.slice(0, 4)}`
        : null,
      lohnsumme: lohnXml ? lohnXml.sepa!.auftragSumme : null,
      anzahlUeberweisungen: lohnXml ? lohnXml.sepa!.auftragAnzahl : null,
      anzahlLohnscheine: scheine.length > 0 ? scheine.length : null,
      // Bei sofortiger Ausführung steht hier der Termin der hochgeladenen
      // Datei, also der Versandtag.
      ausfuehrungstermine: Array.from(new Set(
        xmls.map(d => effektiveTermine.get(d) ?? d.sepa!.ausfuehrung).filter((t): t is string => Boolean(t))
      )).sort(),
      auftraggeberIban: ersteXml ? ersteXml.auftraggeberIban : null
    },
    nichtGeprueft,
    spaeterAuszufuehren
  };
}
